"use client";

import React, { useState, useCallback, useEffect } from "react";
import Image from "next/image";
import styles from "./premium-description.module.css";
import { ListingData, Facilities } from "@/types/types";
import { facilityRules } from "@/lib/getFacilities/getAvailableFacilities"; // Adjust path if needed
import { MdClose } from "react-icons/md";
import {
  IoChevronDownOutline,
  IoChevronUpOutline,
  IoDiamondOutline,
  IoWaterOutline,
  IoShieldCheckmarkOutline,
  IoLeafOutline,
  IoCarOutline,
  IoSnowOutline,
  IoFitnessOutline,
  IoBusinessOutline,
  IoWineOutline,
  IoKeyOutline,
} from "react-icons/io5";
import { MdOutlinePanorama } from "react-icons/md";

interface PremiumDescriptionProps {
  listingData: ListingData;
}

export default function PremiumDescription({
  listingData,
}: PremiumDescriptionProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const description = listingData.description;

  const closeModal = useCallback(() => setIsModalOpen(false), []);

  // Simple scroll lock for the modal
  useEffect(() => {
    if (isModalOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isModalOpen]);

  if (!description) return null;

  const CHARACTER_LIMIT = 400;
  const isLongText = description.length > CHARACTER_LIMIT;

  // =========================================
  // 1. HIGHLIGHTS LOGIC
  // =========================================
  const highlightPool = [
    {
      condition: listingData.is_luxury,
      icon: <IoDiamondOutline />,
      title: "Proprietate Premium",
      text: "Finisaje de excepție și atenție deosebită la detalii arhitecturale.",
    },
    {
      condition: listingData.new_development,
      icon: <IoBusinessOutline />,
      title: "Proiect Nou",
      text: "Construcție recentă, realizată la cele mai înalte standarde moderne.",
    },
    {
      condition: !!listingData.facilities?.has_pool,
      icon: <IoWaterOutline />,
      title: "Piscină Privată",
      text: "Zonă de relaxare exclusivistă cu piscină dedicată proprietății.",
    },
    {
      condition:
        !!listingData.view?.sea_view || !!listingData.view?.mountain_view,
      icon: <MdOutlinePanorama />,
      title: "Vedere Panoramică",
      text: "Orientare privilegiată ce oferă un peisaj vizual spectaculos.",
    },
    {
      condition:
        !!listingData.facilities?.has_security ||
        !!listingData.facilities?.has_gated_community,
      icon: <IoShieldCheckmarkOutline />,
      title: "Securitate & Intimitate",
      text: "Acces restricționat și sisteme avansate pentru confidențialitate totală.",
    },
    {
      condition:
        !!listingData.facilities?.has_garden ||
        !!listingData.facilities?.has_terrace,
      icon: <IoLeafOutline />,
      title: "Grădină & Terase",
      text: "Oază de liniște și natură, perfect integrată în arhitectura proprietății.",
    },
    {
      condition: !!listingData.facilities?.has_wine_cellar,
      icon: <IoWineOutline />,
      title: "Cramă Privată",
      text: "Spațiu dedicat și climatizat pentru colecția dumneavoastră de vinuri.",
    },
    {
      condition:
        !!listingData.facilities?.has_fitness_center ||
        !!listingData.facilities?.has_sauna,
      icon: <IoFitnessOutline />,
      title: "Zonă Wellness",
      text: "Facilități premium dedicate pentru sport, sănătate și relaxare zilnică.",
    },
    {
      condition:
        (!!listingData.facilities?.has_garage ||
          !!listingData.facilities?.has_parking) &&
        listingData.is_luxury,
      icon: <IoCarOutline />,
      title: "Parcare Securizată",
      text: "Spațiu generos și sigur dedicat pentru autoturismele dumneavoastră.",
    },
    {
      condition: !!listingData.facilities?.has_ac,
      icon: <IoSnowOutline />,
      title: "Climatizare Premium",
      text: "Control termic avansat pentru confort absolut în orice sezon.",
    },
    {
      condition: !!listingData.facilities?.has_concierge,
      icon: <IoKeyOutline />,
      title: "Servicii Concierge",
      text: "Asistență dedicată pentru un stil de viață fără compromisuri.",
    },
  ];

  const allActiveHighlights = highlightPool.filter((h) => h.condition);
  const displayHighlights = allActiveHighlights.slice(0, 4);

  // =========================================
  // 2. AMENITIES (FACILITIES) LOGIC
  // =========================================
  const activeFacilities = (
    Object.keys(listingData.facilities || {}) as (keyof Facilities)[]
  ).filter((key) => listingData.facilities[key]);

  return (
    <section className={styles.container}>
      {/* 1. EDITORIAL TEXT BLOCK */}
      <div className={styles.descriptionSection}>
        <h2 className={styles.sectionTitle}>Despre proprietate</h2>

        <div className={styles.textWrapper}>
          <div
            className={`${styles.descriptionText} ${
              !isExpanded && isLongText ? styles.collapsed : ""
            }`}
            dangerouslySetInnerHTML={{ __html: description }}
          />
          {!isExpanded && isLongText && <div className={styles.fadeOverlay} />}
        </div>

        {isLongText && (
          <button
            className={styles.toggleBtn}
            onClick={() => setIsExpanded(!isExpanded)}
            aria-expanded={isExpanded}
          >
            <span>{isExpanded ? "Citește mai puțin" : "Citește mai mult"}</span>
            {isExpanded ? <IoChevronUpOutline /> : <IoChevronDownOutline />}
          </button>
        )}
      </div>

      {/* 2. DYNAMIC HIGHLIGHTS GRID (Top 4) */}
      {displayHighlights.length > 0 && (
        <div className={styles.highlightsContainer}>
          <div className={styles.highlightsGrid}>
            {displayHighlights.map((item, index) => (
              <div key={index} className={styles.highlightCard}>
                <div className={styles.iconWrapper}>{item.icon}</div>
                <div className={styles.highlightContent}>
                  <h4 className={styles.highlightTitle}>{item.title}</h4>
                  <p className={styles.highlightText}>{item.text}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Action to open combined modal */}
          <button
            className={styles.showAllBtn}
            onClick={() => setIsModalOpen(true)}
          >
            Afișează toate punctele forte și facilitățile
          </button>
        </div>
      )}

      {/* =========================================
          3. COMBINED PREMIUM MODAL
          ========================================= */}
      {isModalOpen && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Caracteristici & Facilități</h3>
              <button
                className={styles.closeBtn}
                onClick={closeModal}
                aria-label="Închide"
              >
                <MdClose size={24} />
              </button>
            </div>

            <div className={styles.modalScrollArea}>
              {/* SECTION: ALL HIGHLIGHTS */}
              {allActiveHighlights.length > 0 && (
                <div className={styles.modalSection}>
                  <h4 className={styles.modalSectionTitle}>Puncte forte</h4>
                  <div className={styles.modalHighlightsList}>
                    {allActiveHighlights.map((item, index) => (
                      <div
                        key={`modal-hi-${index}`}
                        className={styles.modalHighlightCard}
                      >
                        <div className={styles.modalIconWrapper}>
                          {item.icon}
                        </div>
                        <div className={styles.highlightContent}>
                          <h4 className={styles.highlightTitle}>
                            {item.title}
                          </h4>
                          <p className={styles.highlightText}>{item.text}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* SECTION: ALL AMENITIES */}
              {activeFacilities.length > 0 && (
                <div className={styles.modalSection}>
                  <h4 className={styles.modalSectionTitle}>Facilități</h4>
                  <ul className={styles.modalFacilitiesGrid}>
                    {activeFacilities.map((facility, index) => {
                      const rule = facilityRules[facility];
                      if (!rule) return null;

                      return (
                        <li key={index} className={styles.facilityItem}>
                          <Image
                            className={styles.facilityIcon}
                            src={rule.icon_url}
                            width={32}
                            height={32}
                            alt={rule.label} // Translate in real app if needed
                            unoptimized
                          />
                          <span className={styles.facilityText}>
                            {rule.label}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
